/**
 * Archivo: src/pages/core-dashboard/CoreDashboardPage.jsx
 * Descripción: Dashboard compatible con el proyecto nuevo sin depender de
 *              TenantContext, RBAC, Breadcrumb ni carpetas platform/layout.
 *
 * Nota:
 * Este archivo reproduce el contenido visual del dashboard original.
 * El sidebar/navbar general debe vivir en un CoreShellLayout si se desea
 * replicar completamente el shell autenticado antiguo.
 */

import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";

const ACTIONS = [
  {
    to: "/usuarios",
    title: "Gestión de Usuarios",
    description: "Altas, edición, estado y asignación de roles.",
    icon: "solar:user-id-outline",
  },
  {
    to: "/core-roles",
    title: "Gestión de Roles",
    description: "Roles, permisos y alcance de operación.",
    icon: "solar:shield-user-outline",
  },
  {
    to: "/contactos",
    title: "Directorio",
    description: "Personas, empresas y contactos del dominio de negocio.",
    icon: "solar:users-group-rounded-outline",
  },
  {
    to: "/perfil",
    title: "Mi Perfil",
    description: "Datos propios y contexto de la sesión activa.",
    icon: "solar:user-linear",
  },
  {
    to: "/superusuarios",
    title: "Superusuarios",
    description: "Administración restringida de cuentas de plataforma.",
    icon: "solar:shield-keyhole-outline",
  },
  {
    to: "/soporte-tecnico",
    title: "Soporte Técnico",
    description: "Herramientas operativas y soporte de tenants.",
    icon: "solar:headphones-round-outline",
  },
];

export default function CoreDashboardPage() {
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    let mounted = true;
    const token = sessionStorage.getItem("syrakis_token");
    const loadFavorites = () => fetch("/api/menu/favorites", { credentials: "same-origin", headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
      .then((response) => response.ok ? response.json() : { favorites: [] })
      .then((data) => { if (mounted) setFavorites((data.favorites || []).flatMap((group) => group.items || [])); })
      .catch(() => { if (mounted) setFavorites([]); });
    loadFavorites();
    window.addEventListener("menu:favorites-updated", loadFavorites);
    return () => { mounted = false; window.removeEventListener("menu:favorites-updated", loadFavorites); };
  }, []);

  return (
    <div className="dashboard-main-body">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <div>
          <h6 className="fw-semibold mb-0">Panel de Control</h6>
          <span className="text-sm text-secondary-light">
            Sinapso Core Platform
          </span>
        </div>
      </div>

      <div className="row gy-4">
        <div className="col-12">
          <section className="card radius-8 border-0 p-20 dashboard-favorites-card">
            <div className="d-flex align-items-center justify-content-between gap-3 mb-12"><div><span className="text-sm text-secondary-light">Accesos rápidos</span><h6 className="mb-0">Favoritos</h6></div><Link to="/menu-core/favoritos" className="btn btn-sm btn-outline-primary">Administrar</Link></div>
            {favorites.length ? <div className="dashboard-favorites-strip">{favorites.map((favorite) => <Link key={favorite.id || favorite.key} to={favorite.path} className="dashboard-favorite-chip"><Icon icon={favorite.icon || "solar:star-linear"} /><span>{favorite.label}</span></Link>)}</div> : <Link to="/menu-core/favoritos" className="dashboard-favorites-empty">Aún no tienes favoritos. Pulsa la estrella junto al título de cualquier página para agregarla.</Link>}
          </section>
        </div>

        {ACTIONS.map((action) => (
          <div
            className="col-xxl-4 col-md-6"
            key={action.to}
          >
            <Link
              to={action.to}
              className="card radius-8 border-0 p-24 h-100 hover-bg-primary-50 d-block text-decoration-none"
            >
              <div className="d-flex align-items-start gap-3">
                <span className="w-48-px h-48-px rounded-circle bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center flex-shrink-0">
                  <Icon
                    icon={action.icon}
                    className="text-2xl"
                  />
                </span>

                <div>
                  <h6 className="mb-8 text-primary-light">
                    {action.title}
                  </h6>

                  <p className="mb-0 text-secondary-light text-sm">
                    {action.description}
                  </p>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
